import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Disclose, EmptyState } from "@lopie/kernel-ui";

import {
  ISSUE_STATUS,
  Msg,
  NoticeList,
  RatingLine,
  STATUS,
  URGENCY,
  addDays,
  fmtDate,
  fmtWhen,
  groupBy,
  money,
  plural,
  useRun,
} from "./shared.jsx";

const dateSlot = { inputLabel: { shrink: true } };

export default function ManagerView({ state, act }) {
  const [tab, setTab] = useState(0);
  const pending = state.bookings.filter((b) => b.status === "requested");
  const openIssues = state.issues.filter((i) => i.status !== "fixed");
  return (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Today" />
        <Tab label={`Bookings (${pending.length} waiting)`} />
        <Tab label="Units & rates" />
        <Tab label="Availability" />
        <Tab label={`Maintenance (${openIssues.length} open)`} />
        <Tab label="Feedback" />
        <Tab label="Settings" />
      </Tabs>
      {tab === 0 ? <Overview state={state} act={act} goto={setTab} /> : null}
      {tab === 1 ? <Bookings state={state} act={act} /> : null}
      {tab === 2 ? <Units state={state} act={act} /> : null}
      {tab === 3 ? <Availability state={state} act={act} /> : null}
      {tab === 4 ? <Maintenance state={state} act={act} /> : null}
      {tab === 5 ? <Feedback state={state} act={act} /> : null}
      {tab === 6 ? <Settings state={state} act={act} /> : null}
    </Stack>
  );
}

function Section({ title, children, empty }) {
  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children && (!Array.isArray(children) || children.length) ? (
        <Stack spacing={1.5}>{children}</Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      )}
    </Box>
  );
}

function Overview({ state, act, goto }) {
  const today = state.me.today;
  const tomorrow = addDays(today, 1);
  const pending = state.bookings.filter((b) => b.status === "requested");
  const arrivingToday = state.bookings.filter((b) => b.status === "approved" && b.start === today);
  const arrivingTomorrow = state.bookings.filter((b) => b.status === "approved" && b.start === tomorrow);
  const inHouse = state.bookings.filter((b) => b.status === "checked_in");
  const urgent = state.issues.filter((i) => i.urgency === "urgent" && i.status !== "fixed");
  return (
    <Stack spacing={3}>
      {!state.units.length ? (
        <Alert severity="info" variant="outlined" action={<Button onClick={() => goto(2)}>Set up units</Button>}>
          You have no units yet. Start in Units &amp; rates, where one button
          creates 5 rentals and 2 campgrounds of 10 sites for you to rename.
        </Alert>
      ) : null}
      <Box sx={{ borderLeft: 2, borderColor: "primary.main", pl: 2 }}>
        <Typography sx={{ fontSize: "3rem", fontWeight: 300, lineHeight: 1.1 }}>{pending.length}</Typography>
        <Typography variant="body1">booking requests are waiting for your decision</Typography>
        <Typography variant="caption" color="text.secondary">
          Counted now, from this app&apos;s own bookings. Dates are in UTC (today is {fmtDate(today)}).
        </Typography>
      </Box>
      <Section title="Waiting for approval" empty="Nothing waiting.">
        {pending.map((b) => <BookingRow key={b.id} b={b} act={act} today={today} />)}
      </Section>
      <Section title="Arriving today" empty="No confirmed arrivals today.">
        {arrivingToday.map((b) => <BookingRow key={b.id} b={b} act={act} today={today} />)}
      </Section>
      <Section title="Arriving tomorrow" empty="No confirmed arrivals tomorrow.">
        {arrivingTomorrow.map((b) => <BookingRow key={b.id} b={b} act={act} today={today} />)}
      </Section>
      <Section title="Checked in now" empty="Nobody is checked in.">
        {inHouse.map((b) => <BookingRow key={b.id} b={b} act={act} today={today} />)}
      </Section>
      <Section title="Urgent maintenance" empty="No urgent issues are open.">
        {urgent.map((i) => <IssueRow key={i.id} issue={i} act={act} />)}
      </Section>
    </Stack>
  );
}

function BookingRow({ b, act, today }) {
  const { run, busy, msg } = useRun(act);
  const [note, setNote] = useState(b.payment_note || "");
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3">
            {b.guest_name} · {b.unit_name}
          </Typography>
          <Typography variant="body2">
            Arrive {fmtDate(b.start)} · leave {fmtDate(b.end)} · {plural(b.nights, "night")} · {money(b.total)} ·{" "}
            {plural(b.party_size || 1, "guest")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {b.guest_email}
            {b.guest_phone ? ` · ${b.guest_phone}` : ""} · signed in as {b.guest}
          </Typography>
          {b.note ? <Typography variant="body2">Note from guest: {b.note}</Typography> : null}
        </Box>
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Chip size="small" variant="outlined" label={STATUS[b.status] || b.status} />
          <Chip size="small" variant="outlined" label={b.paid ? "Paid" : "Not paid"} />
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, mt: 1.5 }}>
        {b.status === "requested" ? (
          <>
            <Button variant="contained" disabled={busy} onClick={() => run("decide", { id: b.id, decision: "approve" }, "Booking confirmed.")}>
              Approve
            </Button>
            <Button variant="outlined" disabled={busy} onClick={() => run("decide", { id: b.id, decision: "decline" }, "Booking declined and the dates released.")}>
              Decline
            </Button>
          </>
        ) : null}
        {b.status === "approved" ? (
          <>
            <Button variant="outlined" disabled={busy} onClick={() => run("remind", { id: b.id }, "Check-in details emailed.")}>
              {b.reminder_sent_at ? "Email check-in details again" : "Email check-in details"}
            </Button>
            {b.start <= today ? null : (
              <Button variant="text" disabled={busy} onClick={() => run("cancel", { id: b.id }, "Booking cancelled.")}>
                Cancel booking
              </Button>
            )}
          </>
        ) : null}
        {b.status === "checked_in" ? (
          <Button variant="outlined" disabled={busy} onClick={() => run("check_out", { id: b.id }, "Marked checked out.")}>
            Mark checked out
          </Button>
        ) : null}
        {["requested", "approved", "checked_in", "checked_out"].includes(b.status) ? (
          <Button
            variant="text"
            disabled={busy}
            onClick={() => run("set_paid", { id: b.id, paid: !b.paid, payment_note: note }, b.paid ? "Marked not paid." : "Marked paid.")}
          >
            {b.paid ? "Mark not paid" : "Mark paid"}
          </Button>
        ) : null}
      </Stack>
      {b.reminder_sent_at ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Check-in details last emailed {fmtWhen(b.reminder_sent_at)}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: "center", maxWidth: "56ch" }}>
        <TextField size="small" label="Payment note" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} />
        <Button variant="text" disabled={busy} onClick={() => run("set_paid", { id: b.id, paid: Boolean(b.paid), payment_note: note }, "Payment note saved.")}>
          Save note
        </Button>
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Msg msg={msg} />
      </Box>
      {(b.notices || []).length ? (
        <Box sx={{ mt: 1 }}>
          <Disclose label={`Messages for this booking (${b.notices.length})`}>
            <NoticeList notices={b.notices} />
          </Disclose>
        </Box>
      ) : null}
    </Paper>
  );
}

const FILTERS = {
  requested: { label: "Waiting for approval", test: (b) => b.status === "requested" },
  upcoming: { label: "Confirmed, not arrived", test: (b) => b.status === "approved" },
  checked_in: { label: "Checked in", test: (b) => b.status === "checked_in" },
  closed: { label: "Checked out, declined or cancelled", test: (b) => ["checked_out", "declined", "cancelled"].includes(b.status) },
  unpaid: { label: "Not marked paid", test: (b) => !b.paid && ["approved", "checked_in", "checked_out"].includes(b.status) },
  all: { label: "All", test: () => true },
};

function Bookings({ state, act }) {
  const [filter, setFilter] = useState(state.bookings.some((b) => b.status === "requested") ? "requested" : "all");
  const list = state.bookings.filter(FILTERS[filter].test).sort((a, b) => (a.start < b.start ? -1 : 1));
  return (
    <Stack spacing={2}>
      <TextField select size="small" label="Show" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ maxWidth: 320 }}>
        {Object.entries(FILTERS).map(([k, v]) => (
          <MenuItem key={k} value={k}>
            {v.label} ({state.bookings.filter(v.test).length})
          </MenuItem>
        ))}
      </TextField>
      {list.length ? (
        list.map((b) => <BookingRow key={b.id} b={b} act={act} today={state.me.today} />)
      ) : (
        <EmptyState title="No bookings match">
          <Typography variant="body2">
            None of this app&apos;s bookings are in &ldquo;{FILTERS[filter].label}&rdquo;.
          </Typography>
        </EmptyState>
      )}
    </Stack>
  );
}

const BLANK_UNIT = {
  name: "",
  property: "",
  category: "rental",
  description: "",
  sleeps: "",
  site_type: "",
  hookups: "",
  price: 100,
  min_nights: 1,
  seasons: [],
  door_code: "",
  wifi_name: "",
  wifi_password: "",
  house_rules: "",
  arrival_info: "",
};

function UnitEditor({ unit, act, onDone }) {
  const [f, setF] = useState(() => ({
    ...BLANK_UNIT,
    ...(unit || {}),
    seasons: (unit?.seasons || []).map((s) => ({ ...s })),
  }));
  const { run, busy, msg } = useRun(act);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const setSeason = (i, k) => (e) =>
    setF((v) => ({ ...v, seasons: v.seasons.map((s, j) => (j === i ? { ...s, [k]: e.target.value } : s)) }));
  const save = async () => {
    const res = await run(
      "save_unit",
      {
        ...f,
        id: unit?.id,
        price: Number(f.price),
        min_nights: Number(f.min_nights),
        seasons: f.seasons.map((s) => ({ ...s, price: Number(s.price) })),
      },
      unit ? "Saved." : "Unit added.",
    );
    if (res && onDone) onDone();
  };
  return (
    <Stack spacing={1.5} sx={{ maxWidth: "64ch", pt: 1 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Name" value={f.name} onChange={set("name")} sx={{ flex: 1 }} />
        <TextField size="small" label="Property or campground" value={f.property} onChange={set("property")} sx={{ flex: 1 }} />
        <TextField select size="small" label="Kind" value={f.category} onChange={set("category")} sx={{ minWidth: 140 }}>
          <MenuItem value="rental">Rental</MenuItem>
          <MenuItem value="campsite">Campsite</MenuItem>
        </TextField>
      </Stack>
      <TextField size="small" label="Description guests see" multiline minRows={2} value={f.description} onChange={set("description")} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Sleeps" value={f.sleeps} onChange={set("sleeps")} />
        <TextField size="small" label="Site type (e.g. tent, RV)" value={f.site_type} onChange={set("site_type")} />
        <TextField size="small" label="Hookups (e.g. 30A, water)" value={f.hookups} onChange={set("hookups")} />
      </Stack>
      <Stack direction="row" spacing={1.5}>
        <TextField size="small" type="number" label="Price a night ($)" value={f.price} onChange={set("price")} />
        <TextField size="small" type="number" label="Minimum nights" value={f.min_nights} onChange={set("min_nights")} />
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Seasonal rates (replace the nightly price on those dates, first match wins)
      </Typography>
      {f.seasons.map((s, i) => (
        <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "center" } }}>
          <TextField size="small" label="Season name" value={s.name || ""} onChange={setSeason(i, "name")} />
          <TextField size="small" type="date" label="From" value={s.start || ""} onChange={setSeason(i, "start")} slotProps={dateSlot} />
          <TextField size="small" type="date" label="Through" value={s.end || ""} onChange={setSeason(i, "end")} slotProps={dateSlot} />
          <TextField size="small" type="number" label="$ a night" value={s.price ?? ""} onChange={setSeason(i, "price")} sx={{ maxWidth: 120 }} />
          <Button variant="text" onClick={() => setF((v) => ({ ...v, seasons: v.seasons.filter((_, j) => j !== i) }))}>
            Remove
          </Button>
        </Stack>
      ))}
      <Box>
        <Button variant="text" onClick={() => setF((v) => ({ ...v, seasons: [...v.seasons, { name: "", start: "", end: "", price: v.price }] }))}>
          Add a seasonal rate
        </Button>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Shown to a guest only after they check in
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Door or gate code" value={f.door_code} onChange={set("door_code")} />
        <TextField size="small" label="Wifi network" value={f.wifi_name} onChange={set("wifi_name")} />
        <TextField size="small" label="Wifi password" value={f.wifi_password} onChange={set("wifi_password")} />
      </Stack>
      <TextField size="small" label="House rules (guests accept these to check in)" multiline minRows={3} value={f.house_rules} onChange={set("house_rules")} />
      <TextField size="small" label="Address and arrival instructions (shown once a booking is confirmed)" multiline minRows={2} value={f.arrival_info} onChange={set("arrival_info")} />
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={busy} onClick={save}>
          {unit ? "Save changes" : "Add unit"}
        </Button>
        {unit ? (
          <Button variant="text" disabled={busy} onClick={() => run("delete_unit", { id: unit.id }, "Unit removed.")}>
            Remove unit
          </Button>
        ) : null}
      </Stack>
      <Msg msg={msg} />
    </Stack>
  );
}

function Units({ state, act }) {
  const { run, busy, msg } = useRun(act);
  const [adding, setAdding] = useState(false);
  return (
    <Stack spacing={2}>
      {!state.units.length ? (
        <EmptyState title="No units yet">
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Typography variant="body2">
              Create the starting set (5 rentals and 2 campgrounds with 10 sites
              each) and then rename and price them, or add units one at a time.
            </Typography>
            <Button variant="contained" disabled={busy} onClick={() => run("seed", {}, "Starter units created. Open each one to rename and price it.")}>
              Create starter units
            </Button>
          </Stack>
        </EmptyState>
      ) : null}
      <Msg msg={msg} />
      <Box>
        <Button variant="outlined" onClick={() => setAdding(!adding)}>
          {adding ? "Close the new unit form" : "Add a unit"}
        </Button>
      </Box>
      {adding ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <UnitEditor act={act} onDone={() => setAdding(false)} />
        </Paper>
      ) : null}
      {groupBy(state.units, (u) => u.property).map(([property, list]) => (
        <Box key={property}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {property} ({plural(list.length, "unit")})
          </Typography>
          <Stack spacing={1}>
            {list.map((u) => (
              <Paper variant="outlined" sx={{ p: 1.5 }} key={u.id}>
                <Disclose
                  label={`${u.name}: ${money(u.price)} a night, minimum ${plural(u.min_nights || 1, "night")}${(u.seasons || []).length ? `, ${plural(u.seasons.length, "seasonal rate")}` : ""}${u.door_code ? "" : ", no door code set"}`}
                >
                  <UnitEditor unit={u} act={act} />
                </Disclose>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const CELL = {
  open: { ch: "·", word: "open" },
  blocked: { ch: "X", word: "blocked" },
  requested: { ch: "R", word: "requested" },
  booked: { ch: "B", word: "booked" },
};

function cellFor(u, d) {
  const r = (u.taken || []).find((t) => d >= t.start && d < t.end);
  if (!r) return CELL.open;
  if (r.why === "blocked") return CELL.blocked;
  if (r.status === "requested") return CELL.requested;
  return CELL.booked;
}

function Strip({ units, today }) {
  const days = Array.from({ length: 30 }, (_, i) => addDays(today, i));
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ borderCollapse: "collapse", fontSize: "0.75rem", fontFamily: "monospace" }}>
        <caption style={{ textAlign: "left", captionSide: "bottom", paddingTop: 6 }}>
          Next 30 nights from {fmtDate(today)} (UTC). · open, R requested, B booked, X blocked.
        </caption>
        <thead>
          <tr>
            <Box component="th" sx={{ textAlign: "left", pr: 1 }}>Unit</Box>
            {days.map((d) => (
              <Box component="th" key={d} sx={{ px: 0.4, fontWeight: 400 }} title={fmtDate(d)}>
                {d.slice(8)}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {units.map((u) => (
            <tr key={u.id}>
              <Box component="th" scope="row" sx={{ textAlign: "left", pr: 1, whiteSpace: "nowrap", fontWeight: 400 }}>
                {u.name}
              </Box>
              {days.map((d) => {
                const c = cellFor(u, d);
                return (
                  <Box
                    component="td"
                    key={d}
                    title={`${u.name}, ${fmtDate(d)}: ${c.word}`}
                    aria-label={`${fmtDate(d)} ${c.word}`}
                    sx={{ textAlign: "center", px: 0.4, border: 1, borderColor: "divider", fontWeight: c.ch === "·" ? 400 : 700 }}
                  >
                    {c.ch}
                  </Box>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function Availability({ state, act }) {
  const properties = useMemo(() => [...new Set(state.units.map((u) => u.property))], [state.units]);
  const [target, setTarget] = useState(properties[0] ? `p:${properties[0]}` : "");
  const [f, setF] = useState({ start: "", end: "", reason: "" });
  const { run, busy, msg } = useRun(act);
  if (!state.units.length) {
    return (
      <EmptyState title="No units yet">
        <Typography variant="body2">Add units under Units &amp; rates first.</Typography>
      </EmptyState>
    );
  }
  const selected = target.startsWith("p:")
    ? state.units.filter((u) => u.property === target.slice(2))
    : state.units.filter((u) => u.id === target.slice(2));
  const blocks = state.blocks
    .filter((b) => selected.some((u) => u.id === b.unit_id) && b.end >= state.me.today)
    .sort((a, b) => (a.start < b.start ? -1 : 1));
  const unitName = (id) => state.units.find((u) => u.id === id)?.name || id;
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Every night is open for booking unless it is booked or you block it.
        Block dates for maintenance, your own use, or a closed season.
      </Typography>
      <TextField select size="small" label="Units" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ maxWidth: 360 }}>
        {properties.map((p) => (
          <MenuItem key={`p:${p}`} value={`p:${p}`}>
            All of {p}
          </MenuItem>
        ))}
        {state.units.map((u) => (
          <MenuItem key={u.id} value={`u:${u.id}`}>
            {u.property}: {u.name}
          </MenuItem>
        ))}
      </TextField>
      <Strip units={selected} today={state.me.today} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h3">Block dates for {target.startsWith("p:") ? `all of ${target.slice(2)}` : selected[0]?.name}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField size="small" type="date" label="First night" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} slotProps={dateSlot} />
          <TextField size="small" type="date" label="Last night" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} slotProps={dateSlot} />
          <TextField size="small" label="Reason (only you see it)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            disabled={busy || !f.start || !f.end}
            onClick={async () => {
              const res = await run("add_block", { unit_ids: selected.map((u) => u.id), ...f }, "Dates blocked.");
              if (res) setF({ start: "", end: "", reason: "" });
            }}
          >
            Block these dates
          </Button>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Msg msg={msg} />
        </Box>
      </Paper>
      <Section title="Upcoming blocked dates" empty="Nothing is blocked for these units.">
        {blocks.map((b) => (
          <Stack key={b.id} direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2">
              {unitName(b.unit_id)}: nights of {fmtDate(b.start)} through {fmtDate(b.end)}
              {b.reason ? ` (${b.reason})` : ""}
            </Typography>
            <Button size="small" variant="text" disabled={busy} onClick={() => run("remove_block", { id: b.id }, "Dates opened again.")}>
              Unblock
            </Button>
          </Stack>
        ))}
      </Section>
    </Stack>
  );
}

function IssueRow({ issue, act }) {
  const { run, busy, msg } = useRun(act);
  const [status, setStatus] = useState(issue.status);
  const [note, setNote] = useState("");
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3">{issue.unit_name}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{issue.description}</Typography>
          <Typography variant="caption" color="text.secondary">
            Reported {fmtWhen(issue.created_at)} by {issue.reporter_name || issue.reporter}
          </Typography>
        </Box>
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Chip size="small" variant="outlined" label={`Urgency: ${URGENCY[issue.urgency] || issue.urgency}`} />
          <Chip size="small" variant="outlined" label={ISSUE_STATUS[issue.status] || issue.status} />
        </Stack>
      </Stack>
      {(issue.history || []).length ? (
        <Stack component="ul" spacing={0.25} sx={{ pl: 3, my: 1 }}>
          {issue.history.map((h, i) => (
            <Typography component="li" variant="body2" key={i}>
              {fmtWhen(h.at)}: {ISSUE_STATUS[h.status] || h.status}
              {h.note ? `, ${h.note}` : ""}
            </Typography>
          ))}
        </Stack>
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5, alignItems: { sm: "center" } }}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
          {Object.entries(ISSUE_STATUS).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Note (the guest sees it)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          disabled={busy}
          onClick={async () => {
            const res = await run("issue_status", { id: issue.id, status, note }, "Issue updated.");
            if (res) setNote("");
          }}
        >
          Update
        </Button>
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Msg msg={msg} />
      </Box>
      {(issue.notices || []).length ? (
        <Box sx={{ mt: 1 }}>
          <Disclose label={`Texts sent about this issue (${issue.notices.length})`}>
            <NoticeList notices={issue.notices} />
          </Disclose>
        </Box>
      ) : null}
    </Paper>
  );
}

function Maintenance({ state, act }) {
  const [filter, setFilter] = useState("active");
  const [f, setF] = useState({ unit_id: "", description: "", urgency: "normal" });
  const { run, busy, msg } = useRun(act);
  const rank = { urgent: 0, normal: 1, low: 2 };
  const list = state.issues
    .filter((i) => (filter === "active" ? i.status !== "fixed" : filter === "fixed" ? i.status === "fixed" : true))
    .sort((a, b) => (rank[a.urgency] - rank[b.urgency]) || (a.created_at < b.created_at ? 1 : -1));
  return (
    <Stack spacing={2}>
      <TextField select size="small" label="Show" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ maxWidth: 260 }}>
        <MenuItem value="active">Open and in progress</MenuItem>
        <MenuItem value="fixed">Fixed</MenuItem>
        <MenuItem value="all">All</MenuItem>
      </TextField>
      {list.length ? (
        list.map((i) => <IssueRow key={i.id} issue={i} act={act} />)
      ) : (
        <EmptyState title="No issues here">
          <Typography variant="body2">No maintenance issues match this filter.</Typography>
        </EmptyState>
      )}
      {state.units.length ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3">Log an issue yourself</Typography>
          <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: "56ch" }}>
            <TextField select size="small" label="Unit" value={f.unit_id} onChange={(e) => setF({ ...f, unit_id: e.target.value })}>
              {state.units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.property}: {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="What is wrong?" multiline minRows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            <TextField select size="small" label="Urgency" value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} sx={{ maxWidth: 200 }}>
              {Object.entries(URGENCY).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Button
                variant="outlined"
                disabled={busy || !f.unit_id || !f.description.trim()}
                onClick={async () => {
                  const res = await run("report_issue", f, "Issue logged.");
                  if (res) setF({ unit_id: "", description: "", urgency: "normal" });
                }}
              >
                Log issue
              </Button>
            </Box>
            <Msg msg={msg} />
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function ReviewRow({ r, act }) {
  const { run, busy, msg } = useRun(act);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h3">{r.unit_name}</Typography>
          <RatingLine summary={{ avg: r.rating, count: 1 }} />
          {r.comment ? <Typography variant="body2">{r.comment}</Typography> : null}
          <Typography variant="caption" color="text.secondary">
            {r.guest_first} ({r.guest}) · {fmtWhen(r.created_at)}
          </Typography>
        </Box>
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Chip size="small" variant="outlined" label={r.hidden ? "Hidden from guests" : "Shown to guests"} />
          <Button size="small" variant="text" disabled={busy} onClick={() => run("review_visibility", { id: r.id, hidden: !r.hidden }, r.hidden ? "Shown to guests again." : "Hidden from guests.")}>
            {r.hidden ? "Show to guests" : "Hide from guests"}
          </Button>
        </Stack>
      </Stack>
      <Msg msg={msg} />
    </Paper>
  );
}

function Feedback({ state, act }) {
  const reviews = [...state.reviews].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  if (!reviews.length) {
    return (
      <EmptyState title="No feedback yet">
        <Typography variant="body2">Guests can rate a stay once they have checked in.</Typography>
      </EmptyState>
    );
  }
  const shown = reviews.filter((r) => !r.hidden);
  const avg = shown.length ? Math.round((shown.reduce((s, r) => s + r.rating, 0) / shown.length) * 10) / 10 : 0;
  return (
    <Stack spacing={2}>
      <Typography variant="body1">
        Average {avg} of 5 across {plural(shown.length, "published rating")}
        {reviews.length > shown.length ? ` (${reviews.length - shown.length} hidden, not counted)` : ""}
      </Typography>
      <Stack spacing={0.5}>
        {state.units.filter((u) => u.rating?.count).map((u) => (
          <Typography variant="body2" key={u.id}>
            {u.property}: {u.name}: {u.rating.avg} of 5 from {plural(u.rating.count, "rating")}
          </Typography>
        ))}
      </Stack>
      {reviews.map((r) => (
        <ReviewRow key={r.id} r={r} act={act} />
      ))}
    </Stack>
  );
}

function Settings({ state, act }) {
  const s = state.settings || {};
  const [phone, setPhone] = useState(s.manager_phone || "");
  const [managers, setManagers] = useState((s.managers || []).join(", "));
  const { run, busy, msg } = useRun(act);
  return (
    <Stack spacing={2} sx={{ maxWidth: "60ch" }}>
      <TextField
        size="small"
        label="Your mobile, for urgent maintenance texts"
        placeholder="+15551234567"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        helperText="With the country code. Left empty, urgent issues still appear here but nobody is texted."
      />
      <TextField
        size="small"
        label="Other property managers (emails, comma separated)"
        value={managers}
        onChange={(e) => setManagers(e.target.value)}
        helperText="They see the manager view instead of the guest view."
      />
      <Box>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() =>
            run(
              "save_settings",
              { manager_phone: phone, managers: managers.split(",").map((m) => m.trim()).filter(Boolean) },
              "Settings saved.",
            )
          }
        >
          Save settings
        </Button>
      </Box>
      <Msg msg={msg} />
      <Disclose label="Who counts as a property manager">
        <Typography variant="body2" color="text.secondary">
          You are signed in as {state.me.username}. A person sees the manager
          view if they are an owner set when the app was installed
          ({(s.owners || []).join(", ") || "none"}) or are listed above.
          Everyone else sees the guest view. In a test copy, a test user signs
          in as sandbox-&lt;request id&gt;-&lt;name&gt; and is matched by the
          name after that prefix. Emails and texts are handed to Kernel to
          send; being accepted is not proof they were delivered.
        </Typography>
      </Disclose>
    </Stack>
  );
}
